import { useRef, useState } from 'react';

import {
  TertiaryButton,
  Typography,
} from '@changegps/horizon-component-library';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';

import { useDeleteImage, useUserDetailsFormContext } from '../hooks';
import { UserDetails } from '../types';

import { DeleteUserImageDialog } from './DeleteUserImageDialog';

interface UserImagesProps {
  user?: UserDetails;
}

export const UserImages = ({ user }: UserImagesProps) => {
  const form = useUserDetailsFormContext();
  const { mutate: deleteImage } = useDeleteImage(user?.uuid ?? '');

  // Refs for file inputs
  const profileInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  // State for preview URLs
  const [profilePreview, setProfilePreview] = useState<string | null>('');
  const [signaturePreview, setSignaturePreview] = useState<string | null>('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileType, setFileType] = useState<'profile' | 'signature' | null>(
    null,
  );

  const [isDeleting, setIsDeleting] = useState(false);

  // Handle file selection
  const handleUpload =
    (type: 'profile' | 'signature') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Generate temporary URL for preview
        const previewUrl = URL.createObjectURL(file);

        if (type === 'profile') {
          setProfilePreview(previewUrl);
          form.setValue('profilePic', file); // Bind to form state
        } else {
          setSignaturePreview(previewUrl);
          form.setValue('signaturePic', file); // Bind to form state
        }

        // Clean up object URL to avoid memory leaks
        return () => URL.revokeObjectURL(previewUrl);
      }
    };

  const handleDelete = (type: 'profile' | 'signature') => () => {
    if (type === 'profile') {
      if (profilePreview) {
        form.unregister('profilePic'); // Remove from form state
        setProfilePreview('');
        if (profileInputRef.current) {
          profileInputRef.current.value = ''; // Reset input field
        }
      } else {
        setFileType(type);
        setDialogOpen(true);
      }
    } else if (type === 'signature') {
      if (signaturePreview) {
        form.unregister('signaturePic'); // Remove from form state
        setSignaturePreview('');
        if (signatureInputRef.current) {
          signatureInputRef.current.value = ''; // Reset input field
        }
      } else {
        setFileType(type);
        setDialogOpen(true);
      }
    }
  };
  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (!fileType) return;

    setIsDeleting(true);

    deleteImage(
      { file: `${fileType}_pic`, uuid: user?.uuid },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setIsDeleting(false);
          toast.success(`Successfully deleted user ${fileType}`);
        },
        onSettled: () => {
          if (fileType === 'profile') {
            form.unregister('profilePic'); // Remove profile_pic from form state
            setProfilePreview('');
            if (profileInputRef.current) {
              profileInputRef.current.value = '';
            }
          } else {
            form.unregister('signaturePic'); // Remove signature_pic from form state
            setSignaturePreview('');
            if (signatureInputRef.current) {
              signatureInputRef.current.value = '';
            }
          }
        },
        onError: () => {
          toast.error(`Error deleting user ${fileType}`);
        },
      },
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '48px',
      }}
    >
      {/* Profile Picture Section */}
      <Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            marginY: '0.5em',
          }}
        >
          Profile Picture
        </Typography>
        {profilePreview || user?.profilePic ? (
          <img
            src={profilePreview || user?.profilePic || ''}
            alt="Profile"
            style={{
              border: '2px solid #EFEFEF',
              width: '200px',
              height: '200px',
              borderRadius: '10px',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Typography
            sx={{
              width: '200px',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #ccc',
              borderRadius: '10px',
              color: '#888',
            }}
          >
            No Profile Uploaded
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginY: '1em',
            gap: '4px',
          }}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg" // Allow JPG, JPEG, PNG
            style={{ display: 'none' }}
            ref={profileInputRef}
            onChange={handleUpload('profile')}
          />
          {/* Trigger file input */}
          <TertiaryButton
            fullWidth
            onClick={() => profileInputRef.current?.click()}
          >
            Upload
          </TertiaryButton>
          <TertiaryButton
            fullWidth
            onClick={handleDelete('profile')}
            disabled={!profilePreview && !user?.profilePic}
          >
            Delete
          </TertiaryButton>
        </Box>
      </Box>

      {/* Signature Section */}
      <Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            marginY: '0.5em',
          }}
        >
          Signature
        </Typography>
        {signaturePreview || user?.signaturePic ? (
          <img
            src={signaturePreview || user?.signaturePic || ''}
            alt="Signature"
            style={{
              border: '2px solid #EFEFEF',
              width: '200px',
              height: '200px',
              borderRadius: '10px',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Typography
            sx={{
              width: '200px',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #ccc',
              borderRadius: '10px',
              color: '#888',
            }}
          >
            No Signature Uploaded
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginY: '1em',
            gap: '4px',
          }}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="image/png"
            style={{ display: 'none' }}
            ref={signatureInputRef}
            onChange={handleUpload('signature')}
          />
          {/* Trigger file input */}
          <TertiaryButton
            fullWidth
            onClick={() => signatureInputRef.current?.click()}
          >
            Upload
          </TertiaryButton>
          <TertiaryButton
            fullWidth
            onClick={handleDelete('signature')}
            disabled={!signaturePreview && !user?.signaturePic}
          >
            Delete
          </TertiaryButton>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      {fileType && (
        <DeleteUserImageDialog
          open={dialogOpen}
          isPending={isDeleting}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          imageType={fileType}
        />
      )}
    </Box>
  );
};
